import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Heart, Calendar, MapPin, Users, Sparkles, Loader2, ArrowRight,
  ShieldCheck, TrendingUp, Share2, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/**
 * GiveBackDirectory — public, shareable `/give-back` page listing every
 * active fundraiser event on AddVendTour. Designed to double as a press-kit
 * URL: the platform-wide impact strip up top gives journalists a headline
 * stat block, and every card links straight to the event's donation flow.
 */
export default function GiveBackDirectory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/give-back/events", { params: { limit: 60 } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ upcoming: [], past: [], impact: {} }))
      .finally(() => setLoading(false));
  }, []);

  const shareUrl = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/give-back`);
      toast.success("Share link copied");
    } catch { toast.error("Copy failed"); }
  };

  const filter = (arr) => {
    const s = q.trim().toLowerCase();
    if (!s) return arr;
    return arr.filter((e) =>
      (e.title || "").toLowerCase().includes(s) ||
      (e.beneficiary_org_name || "").toLowerCase().includes(s) ||
      (e.city || "").toLowerCase().includes(s)
    );
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-20 text-center" data-testid="give-back-loading">
        <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-pink)" }} />
      </div>
    );
  }
  const impact = data?.impact || {};
  const upcoming = filter(data?.upcoming || []);
  const past = filter(data?.past || []);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="give-back-page">
      {/* Hero */}
      <section className="text-center py-10">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
             style={{ backgroundColor: "rgba(236,72,153,0.10)", color: "var(--avt-pink)",
                      border: "1.5px solid var(--avt-pink)" }}>
          <Heart className="h-3 w-3" fill="var(--avt-pink)" strokeWidth={0} />
          The Give Back Marketplace
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-tight" data-testid="give-back-title">
          <span style={{ color: "var(--avt-teal)" }}>Every</span>{" "}
          <span style={{ color: "var(--avt-orange)" }}>market</span>{" "}
          <span style={{ color: "var(--avt-purple)" }}>can</span>{" "}
          <span style={{ color: "var(--avt-pink)" }}>give back.</span>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-base leading-relaxed"
           style={{ color: "var(--ink-soft)" }}>
          Local events partnered with verified 501(c)(3) nonprofits.
          Buy from a maker. Grab a bite. Drop a donation. Every dollar goes 100%
          to the cause &mdash; AddVendTour takes 0%.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/nonprofits" className="btn-primary inline-flex items-center gap-2 px-6 py-2.5 rounded-full"
                data-testid="give-back-nonprofits-cta">
            <Sparkles className="h-4 w-4" /> Browse nonprofits
          </Link>
          <Button variant="outline" onClick={shareUrl} data-testid="give-back-share"
                  className="rounded-full">
            <Share2 className="h-4 w-4 mr-2" /> Share this page
          </Button>
        </div>
      </section>

      {/* Impact strip */}
      <section
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 py-8 border-y"
        style={{ borderColor: "var(--border-warm)" }}
        data-testid="give-back-impact"
      >
        <Impact color="var(--avt-purple)" icon={Heart}
                value={`$${((impact.total_raised_cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                label="Raised across AddVendTour" testId="impact-raised" />
        <Impact color="var(--avt-pink)" icon={Users}
                value={impact.donor_count || 0}
                label="Supporters chipping in" testId="impact-donors" />
        <Impact color="var(--avt-teal)" icon={Calendar}
                value={impact.active_fundraisers || 0}
                label="Active fundraisers this month" testId="impact-fundraisers" />
        <Impact color="var(--avt-orange)" icon={ShieldCheck}
                value={impact.nonprofits_benefiting || 0}
                label="Nonprofits benefiting" testId="impact-nonprofits" />
      </section>

      {/* Search */}
      <div className="mt-10 max-w-md mx-auto">
        <Input
          data-testid="give-back-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search events, nonprofits, or cities…"
        />
      </div>

      {/* Upcoming */}
      <section className="mt-10" data-testid="give-back-upcoming-section">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="h-4 w-4" style={{ color: "var(--avt-teal)" }} />
          <h2 className="font-serif text-2xl">Upcoming fundraisers</h2>
          <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
            · {upcoming.length}
          </span>
        </div>
        {upcoming.length === 0 ? (
          <div className="card-warm rounded-2xl p-10 text-center" data-testid="give-back-empty-upcoming">
            <Heart className="h-8 w-8 mx-auto" style={{ color: "var(--avt-pink)" }} />
            <p className="mt-4 text-sm" style={{ color: "var(--ink-soft)" }}>
              No fundraisers scheduled right now.{" "}
              <Link to="/organizer/dashboard" className="underline" style={{ color: "var(--avt-purple)" }}>
                Post the first one
              </Link>{" "}
              or{" "}
              <Link to="/nonprofits" className="underline" style={{ color: "var(--avt-purple)" }}>
                donate to a nonprofit directly
              </Link>.
            </p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {upcoming.map((e) => <FundraiserCard key={e.id} event={e} />)}
          </div>
        )}
      </section>

      {/* Past */}
      {past.length > 0 && (
        <section className="mt-14" data-testid="give-back-past-section">
          <div className="flex items-center gap-2 mb-6">
            <h2 className="font-serif text-2xl" style={{ color: "var(--ink-soft)" }}>Past fundraisers</h2>
            <span className="text-xs" style={{ color: "var(--ink-soft)" }}>· {past.length}</span>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {past.map((e) => <FundraiserCard key={e.id} event={e} muted />)}
          </div>
        </section>
      )}

      {/* CTA — organizers */}
      <section className="mt-16 card-warm rounded-2xl p-8 text-center"
               style={{ background: "linear-gradient(135deg, rgba(236,72,153,0.06), rgba(107,47,186,0.06))" }}>
        <h3 className="font-serif text-3xl">Host a fundraiser at your next event.</h3>
        <p className="mt-3 max-w-xl mx-auto text-sm" style={{ color: "var(--ink-soft)" }}>
          Any organizer can flag their market as a fundraiser and pick a verified 501(c)(3)
          as the beneficiary. Free to activate. Donations settle straight into the nonprofit's account.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/organizer/events/new" className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full"
                data-testid="give-back-host-cta">
            <Sparkles className="h-4 w-4" /> Host a fundraiser
          </Link>
          <Link to="/verify-nonprofit" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border"
                style={{ borderColor: "var(--border-warm)", color: "var(--ink)" }}
                data-testid="give-back-verify-cta">
            <ShieldCheck className="h-4 w-4" /> Verify your nonprofit
          </Link>
        </div>
      </section>
    </div>
  );
}

function Impact({ icon: Icon, color, value, label, testId }) {
  return (
    <div className="text-center" data-testid={testId}>
      <div className="mx-auto h-10 w-10 rounded-full flex items-center justify-center"
           style={{ backgroundColor: `${color}18` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="mt-3 font-serif text-3xl" style={{ color }}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest mt-1" style={{ color: "var(--ink-soft)" }}>
        {label}
      </div>
    </div>
  );
}

function FundraiserCard({ event, muted }) {
  const raised = event.fundraising_raised_cents || 0;
  const goal = event.fundraising_goal_cents;
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : null;

  return (
    <Link
      to={`/events/${event.id}`}
      data-testid={`give-back-card-${event.id}`}
      className="card-warm rounded-2xl overflow-hidden flex flex-col transition-all hover:-translate-y-1"
      style={{ boxShadow: muted ? "none" : "0 0 0 1px rgba(236,72,153,0.30)", opacity: muted ? 0.75 : 1 }}
    >
      {event.cover_image ? (
        <div className="aspect-[16/9] overflow-hidden">
          <img src={event.cover_image} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-[16/9] flex items-center justify-center"
             style={{ background: "linear-gradient(135deg, rgba(236,72,153,0.10), rgba(107,47,186,0.10))" }}>
          <Heart className="h-10 w-10" style={{ color: "var(--avt-pink)" }} fill="var(--avt-pink)" strokeWidth={0} />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full self-start"
             style={{
               backgroundColor: "rgba(236,72,153,0.14)",
               color: "var(--avt-pink)",
               border: "1px solid var(--avt-pink)",
             }}>
          <Heart className="h-3 w-3" fill="var(--avt-pink)" strokeWidth={0} /> Fundraiser
        </div>
        <div className="font-serif text-lg mt-2 leading-snug" data-testid={`give-back-card-title-${event.id}`}>
          {event.title}
        </div>
        <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
          Benefits <strong style={{ color: "var(--avt-purple)" }}>{event.beneficiary_org_name || "verified 501(c)(3)"}</strong>
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {event.date}</span>
          {event.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {event.city}</span>}
        </div>

        {/* Progress */}
        <div className="mt-4 flex-1">
          <div className="flex items-baseline justify-between">
            <span className="font-serif text-xl" style={{ color: "var(--avt-purple)" }}
                  data-testid={`give-back-raised-${event.id}`}>
              ${(raised / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
            <span className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
              {event.fundraising_donor_count || 0} supporters
              {goal ? <> · goal ${(goal / 100).toLocaleString()}</> : null}
            </span>
          </div>
          {goal && (
            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(0,0,0,0.06)" }}>
              <div style={{
                width: `${pct}%`, height: "100%",
                background: "linear-gradient(90deg, var(--avt-pink), var(--avt-purple))",
              }} />
            </div>
          )}
        </div>

        <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold"
             style={{ color: "var(--avt-pink)" }}>
          Donate + attend <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}
